import * as React from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";

interface WelcomeScreenProps {
  onContinue: () => void;
}

/** Paths are served from copied `assets/icons/*` (see webpack CopyWebpackPlugin). */
const WELCOME_LEGAL_LOGOS: readonly { src: string; alt: string }[] = [
  { src: "assets/icons/euipo-logo.jpg", alt: "EUIPO" },
  { src: "assets/icons/eur-lex-logo.png", alt: "EUR-Lex" },
  { src: "assets/icons/boe-logo.svg", alt: "BOE" },
  { src: "assets/icons/cendoj-logo.png", alt: "CENDOJ" },
  { src: "assets/icons/oepm-logo.svg", alt: "OEPM" },
  { src: "assets/icons/curia-logo.svg", alt: "CURIA" },
  { src: "assets/icons/aepd-logo.png", alt: "AEPD" },
];

const legalLogoClassName =
  "h-4 w-auto max-w-[3.25rem] sm:max-w-[3.5rem] object-contain object-center opacity-[0.88]";

const WelcomeScreen: React.FC<WelcomeScreenProps> = ({ onContinue }) => {
  const [reduceMotion, setReduceMotion] = React.useState(false);

  React.useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduceMotion(mq.matches);
    const onChange = () => setReduceMotion(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-white p-6 sm:p-8 box-border animate-fade-in">
      <div className="w-full max-w-sm flex flex-col items-center text-center space-y-8">
        <div className="space-y-6 w-full">
          <div className="inline-flex items-center justify-center gap-2.5">
            <img
              src="assets/workops-favicon-light.svg"
              alt=""
              width={32}
              height={32}
              className="flex-shrink-0 w-8 h-8"
            />
            <span className="text-lg font-semibold text-slate-900 tracking-tight">WorkOps</span>
          </div>

          <div className="space-y-1.5">
            <h1 className="text-2xl font-semibold text-slate-900 tracking-tight m-0">Welcome</h1>
            <p className="text-sm text-slate-500 m-0 leading-relaxed">
              Sign in to your account to continue
            </p>
            <p className="text-sm text-slate-500 m-0 leading-relaxed pt-1">
              AI-powered document intelligence that works where you work.
            </p>
          </div>
        </div>

        <div className="w-full space-y-2">

          <div className="w-full rounded-lg border border-slate-200/90 bg-slate-50/90 px-2.5 py-2">
            <div className="flex items-center gap-2 min-h-[2.25rem]">
              <div className="flex shrink-0 items-center gap-1.5">
                <img
                  src="assets/icons/microsoft-word-icon.svg"
                  alt=""
                  width={22}
                  height={22}
                  className="size-[22px] opacity-90 flex-shrink-0"
                />
                <img
                  src="assets/icons/microsoft-excel-logo.svg"
                  alt=""
                  width={22}
                  height={22}
                  className="size-[22px] opacity-90 flex-shrink-0"
                />
              </div>

              <div className="w-px h-6 bg-slate-200 shrink-0 self-center" aria-hidden />

              <div className="min-w-0 flex-1 flex items-center py-0.5">
                {reduceMotion ? (
                  <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 w-full">
                    {WELCOME_LEGAL_LOGOS.map((logo) => (
                      <div key={logo.src} className="flex shrink-0 items-center leading-none">
                        <img src={logo.src} alt={logo.alt} className={legalLogoClassName} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="welcome-legal-marquee-mask relative w-full overflow-hidden">
                    <div className="welcome-legal-marquee-track">
                      {[...WELCOME_LEGAL_LOGOS, ...WELCOME_LEGAL_LOGOS].map((logo, idx) => (
                        <div key={`${logo.src}-${idx}`} className="flex shrink-0 items-center leading-none">
                          <img src={logo.src} alt={logo.alt} className={legalLogoClassName} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <p className="text-[9px] leading-snug text-slate-400 text-center m-0 mt-1.5 pt-1.5 border-t border-slate-200/80">
              Also works with Microsoft 365 and SharePoint.
            </p>
          </div>
        </div>

        <Button type="button" onClick={onContinue} className="w-full" size="lg">
          Sign in
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export default WelcomeScreen;
